import { supabase } from '../lib/supabase';

// ============ TYPES ============
export interface Empresa {
  id: string;
  nombre: string;
  slug: string;
  email?: string;
  telefono?: string;
  direccion?: string;
  ciudad?: string;
  pais: string;
  logo_url?: string;
  colores: {
    primario: string;
    secundario: string;
    acento: string;
  };
  descripcion?: string;
  activa: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Producto {
  id: string;
  empresaId: string;
  nombre: string;
  descripcion?: string;
  sku?: string;
  categoria?: string;
  precio_compra: number;
  precio_venta: number;
  precio_sin_iva: number;
  valor_iva: number;
  iva_porcentaje: number;
  stock: number;
  stock_minimo: number;
  unidad_medida: string;
  estado: string;
  imagen?: string;
  mostrarEnCatalogo: boolean;
  destacado: boolean;
  disponibleOnline: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Cliente {
  id: string;
  empresaId: string;
  nombre: string;
  apellido?: string;
  telefono?: string;
  email?: string;
  direccion?: string;
  ciudad?: string;
  tipo_documento: string;
  numero_documento?: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Venta {
  id: string;
  empresaId: string;
  cliente_id?: string;
  numero: string;
  fecha: string;
  subtotal: number;
  impuestos: number;
  descuentos: number;
  total: number;
  metodo_pago?: string;
  estado: string;
  notas?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ItemVenta {
  id: string;
  venta_id: string;
  producto_id?: string;
  cantidad: number;
  precio_unitario: number;
  descuento: number;
  subtotal: number;
  createdAt: string;
}

export interface Factura {
  id: string;
  empresaId: string;
  cliente_id?: string;
  numero: string;
  /** Use created_at (DB column); fecha is invoice date when present */
  created_at?: string;
  fecha?: string;
  fecha_vencimiento?: string;
  fecha_entrega?: string;
  fecha_pago?: string;
  subtotal: number;
  impuestos: number;
  descuentos: number;
  total: number;
  estado: string;
  estado_pago: string;
  metodo_pago?: string;
  monto_pagado?: number;
  notas?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ItemFactura {
  id: string;
  factura_id: string;
  producto_id?: string;
  nombre: string;
  descripcion?: string;
  cantidad: number;
  precio_unitario: number;
  descuento: number;
  impuesto: number;
  subtotal: number;
  total: number;
  createdAt: string;
}

export interface Cita {
  id: string;
  empresaId: string;
  cliente_id?: string;
  titulo: string;
  descripcion?: string;
  fecha: string;
  estado: string;
  telefono_contacto?: string;
  whatsapp?: string;
  precio?: number;
  notas?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConfiguracionEmpresa {
  id: string;
  empresaId: string;
  whatsapp_config: any;
  instagram_config: any;
  branding_config: any;
  contacto_publico_config: any;
  notificaciones_config: any;
  createdAt: string;
  updatedAt: string;
}

// ============ EMPRESAS ============
export const empresaService = {
  async getAll(): Promise<Empresa[]> {
    const { data, error } = await supabase
      .from('empresas')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Empresa | null> {
    const { data, error } = await supabase
      .from('empresas')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async getBySlug(slug: string): Promise<Empresa | null> {
    const { data, error } = await supabase
      .from('empresas')
      .select('*')
      .eq('slug', slug)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(empresa: Partial<Empresa>): Promise<Empresa> {
    const { data, error } = await supabase
      .from('empresas')
      .insert(empresa)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, empresa: Partial<Empresa>): Promise<Empresa> {
    const { data, error } = await supabase
      .from('empresas')
      .update(empresa)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('empresas')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// ============ PRODUCTOS ============
export const productoService = {
  async getAll(empresaId: string): Promise<Producto[]> {
    const { data, error } = await supabase
      .from('productos')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Producto | null> {
    const { data, error } = await supabase
      .from('productos')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async getForCatalog(empresaId: string): Promise<Producto[]> {
    const { data, error } = await supabase
      .from('productos')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('mostrar_en_catalogo', true)
      .or('estado.eq.ACTIVO,estado.is.null')
      .order('destacado', { ascending: false })
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async create(producto: Partial<Producto>): Promise<Producto> {
    const { data, error } = await supabase
      .from('productos')
      .insert(producto)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, producto: Partial<Producto>): Promise<Producto> {
    const { data, error } = await supabase
      .from('productos')
      .update(producto)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('productos')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// ============ CLIENTES ============
export const clienteService = {
  async getAll(empresaId: string): Promise<Cliente[]> {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Cliente | null> {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(cliente: Partial<Cliente>): Promise<Cliente> {
    const { data, error } = await supabase
      .from('clientes')
      .insert(cliente)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, cliente: Partial<Cliente>): Promise<Cliente> {
    const { data, error } = await supabase
      .from('clientes')
      .update(cliente)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('clientes')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// ============ VENTAS ============
export const ventaService = {
  async getAll(empresaId: string): Promise<Venta[]> {
    const { data, error } = await supabase
      .from('ventas')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Venta | null> {
    const { data, error } = await supabase
      .from('ventas')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(venta: Partial<Venta>): Promise<Venta> {
    const { data, error } = await supabase
      .from('ventas')
      .insert(venta)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, venta: Partial<Venta>): Promise<Venta> {
    const { data, error } = await supabase
      .from('ventas')
      .update(venta)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('ventas')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// ============ FACTURAS ============
export const facturaService = {
  async getAll(empresaId: string): Promise<Factura[]> {
    const { data, error } = await supabase
      .from('facturas')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Factura | null> {
    const { data, error } = await supabase
      .from('facturas')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(factura: Partial<Factura>): Promise<Factura> {
    const { data, error } = await supabase
      .from('facturas')
      .insert(factura)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, factura: Partial<Factura>): Promise<Factura> {
    const { data, error } = await supabase
      .from('facturas')
      .update(factura)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('facturas')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// ============ CITAS ============
export const citaService = {
  async getAll(empresaId: string): Promise<Cita[]> {
    const { data, error } = await supabase
      .from('citas')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('fecha', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<Cita | null> {
    const { data, error } = await supabase
      .from('citas')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(cita: Partial<Cita>): Promise<Cita> {
    const { data, error } = await supabase
      .from('citas')
      .insert(cita)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, cita: Partial<Cita>): Promise<Cita> {
    const { data, error } = await supabase
      .from('citas')
      .update(cita)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('citas')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// ============ CONFIGURACION EMPRESA ============
export const configuracionService = {
  async getByEmpresaId(empresaId: string): Promise<ConfiguracionEmpresa | null> {
    const { data, error } = await supabase
      .from('configuracion_empresa')
      .select('*')
      .eq('empresa_id', empresaId)
      .single();
    
    if (error) throw error;
    return data;
  },

  async create(configuracion: Partial<ConfiguracionEmpresa>): Promise<ConfiguracionEmpresa> {
    const { data, error } = await supabase
      .from('configuracion_empresa')
      .insert(configuracion)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(empresaId: string, configuracion: Partial<ConfiguracionEmpresa>): Promise<ConfiguracionEmpresa> {
    const { data, error } = await supabase
      .from('configuracion_empresa')
      .update(configuracion)
      .eq('empresa_id', empresaId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};



